import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { FilterField } from '../generic-list/filter-field.model';

export interface Invoice {
  id?: string;
  createdAt?: string;
  start_date?: string;
  end_date?: string;
  amount: number;
  status?: string;
}

export interface InvoiceAction {
  action: string;
  invoice: Invoice;
}

export interface PendingAction {
  action: string;
  invoice: Invoice;
}

@Component({
  selector: 'app-btb-invoices-list',
  templateUrl: './btb-invoices-list.component.html',
  styleUrls: ['./btb-invoices-list.component.scss']
})
export class BtbInvoicesListComponent implements OnInit, OnChanges {
  @Input() allInvoices: Invoice[] = [];
  @Input() filterFields: FilterField[] = [];
  @Input() showAddButton: boolean = false;
  @Input() isBackoffice: boolean = false;
  @Input() pendingTotal: number = 0;
  @Input() totalBilling: number = 0;
  @Output() invoiceAction = new EventEmitter<InvoiceAction>();
  @Output() addInvoice = new EventEmitter<void>();
  @Output() filteredCountChange = new EventEmitter<{type: string, count: number}>();

  processingInvoices: Set<string> = new Set();
  isAnyOperationInProgress: boolean = false;
  
  showConfirmationModal: boolean = false;
  pendingAction: PendingAction | null = null;

  ngOnInit() {
    console.log('All Invoices:', this.allInvoices);
  }

  ngOnChanges() {
    console.log('All Invoices changed:', this.allInvoices);
  }

  onInvoiceAction(action: string, invoice: Invoice) {
    if (this.isAnyOperationInProgress) {
      return;
    }
    
    // Check if this is a status change action that requires confirmation
    const statusChangeActions = ['mark_as_pending', 'mark_as_settled', 'confirm_invoice', 'cancel_invoice'];
    
    if (statusChangeActions.includes(action)) {
      this.pendingAction = { action, invoice };
      this.showConfirmationModal = true;
    } else {
      // For non-status actions (like download), execute immediately
      this.processingInvoices.add(invoice.id!);
      this.isAnyOperationInProgress = true;
      this.invoiceAction.emit({ action, invoice });
    }
  }

  isProcessing(invoiceId: string): boolean {
    return this.processingInvoices.has(invoiceId);
  }

  stopProcessing(invoiceId: string) {
    this.processingInvoices.delete(invoiceId);
    this.isAnyOperationInProgress = this.processingInvoices.size > 0;
  }

  onAddInvoice() {
    this.addInvoice.emit();
  }

  getSettledInvoiceActions(): string[] {
    if (this.isBackoffice) {
      return ['download', 'mark_as_pending', 'cancel_invoice'];
    } else {
      return ['download'];
    }
  }

  getPendingInvoiceActions(invoice: Invoice): string[] {
    if (this.isBackoffice) {
      if (invoice.status === 'PENDING' || invoice.status === 'PENDING_CONFIRMED') {
        return ['download', 'mark_as_settled', 'cancel_invoice'];
      } else if (invoice.status === 'CANCELLED') {
        return ['download', 'mark_as_pending'];
      } else {
        return ['download'];
      }
    } else {
      if (invoice.status === 'PENDING') {
        return ['download', 'confirm_invoice', 'cancel_invoice'];
      } else {
        return ['download'];
      }
    }
  }

  onFilteredCountChange(type: string, count: number) {
    this.filteredCountChange.emit({ type, count });
  }
  

  canPerformAction(): boolean {
    return !this.isAnyOperationInProgress;
  }

  getNewStatus(): string {
    if (!this.pendingAction) return '';
    
    const action = this.pendingAction.action;
    const currentStatus = this.pendingAction.invoice.status;
    
    switch (action) {
      case 'mark_as_pending':
        return 'PENDING';
      case 'mark_as_settled':
        return 'SETTLED';
      case 'confirm_invoice':
        return 'PENDING_CONFIRMED';
      case 'cancel_invoice':
        return 'CANCELLED';
      default:
        return currentStatus || '';
    }
  }

  confirmStatusChange() {
    if (this.pendingAction) {
      this.showConfirmationModal = false;
      this.processingInvoices.add(this.pendingAction.invoice.id!);
      this.isAnyOperationInProgress = true;
      this.invoiceAction.emit({ 
        action: this.pendingAction.action, 
        invoice: this.pendingAction.invoice 
      });
      this.pendingAction = null;
    }
  }

  cancelStatusChange() {
    this.showConfirmationModal = false;
    this.pendingAction = null;
  }
}
